import type { Issue, PageLayout } from './layout.js';
import type { FontProvider } from './fonts.js';
import type { ProjectDoc } from './schema.js';
import { calendarPages } from './project.js';

/** Project-level artwork checks (page-level ones come from layoutPage). */
export function projectChecks(doc: ProjectDoc, fonts: FontProvider): Issue[] {
  const issues: Issue[] = [];
  for (const s of doc.eventSets) {
    if (!s.display.visible) continue;
    if (s.verification !== 'verified' && (s.kind === 'public' || s.kind === 'school'))
      issues.push({
        id: `project:eventset:${s.id}:unverified`,
        severity: 'warning',
        code: 'unverified-events',
        message: `"${s.name}" is ${s.verification === 'manual' ? 'manually entered' : 'not verified'} against an official source. Confirm the dates before approval.`,
      });
    if (s.year !== null && s.year !== doc.year)
      issues.push({ id: `project:eventset:${s.id}:year`, severity: 'error', code: 'wrong-year-events', message: `"${s.name}" is for ${s.year} but the calendar is ${doc.year}.` });
  }
  for (const [fontId, sha] of Object.entries(doc.fontLock)) {
    const f = fonts.get(fontId);
    if (f && f.meta.sha256 !== sha)
      issues.push({ id: `project:font:${fontId}:changed`, severity: 'warning', code: 'font-changed', message: `Font "${f.meta.family} ${f.meta.weight}" differs from the file used when this project was saved. Text may reflow.` });
  }
  if (doc.year < 1900 || doc.year > 2100) issues.push({ id: 'project:year', severity: 'error', code: 'bad-year', message: 'Year must be between 1900 and 2100.' });
  if (doc.product.bleedMm === 0)
    issues.push({ id: 'project:no-bleed', severity: 'info', code: 'no-bleed-mode', message: `Printing without bleed at actual size: keep text and logos ${doc.product.safeMarginMm} mm from the edge; nothing will be trimmed.` });
  else if (doc.product.bleedMm < 2) issues.push({ id: 'project:bleed', severity: 'warning', code: 'small-bleed', message: `Bleed is ${doc.product.bleedMm} mm; most print shops require at least 3 mm.` });
  const p = doc.product;
  const expected = p.productType === 'year' ? 1 : p.productType === 'tent' ? (p.tent.layout === 'classic' ? 2 : 3) : 12 + (p.includeCover ? (p.duplex ? 2 : 1) : 0);
  if (calendarPages(doc).length !== expected)
    issues.push({ id: 'project:pages', severity: 'error', code: 'page-count', message: 'Unexpected number of pages.' });
  if (p.productType === 'monthly' && p.duplex && !p.includeCover)
    issues.push({ id: 'project:duplex-cover', severity: 'info', code: 'duplex-pairs', message: 'Double-sided leaves: Jan–Jun on the fronts, Jul–Dec on the backs (6 leaves). Add a cover for 7 leaves with a back cover.' });
  if (p.productType === 'tent' && p.tent.layout === 'classic') {
    const face = (p.heightMm - p.tent.baseMm - p.tent.tabMm) / 2;
    if (face < 40) issues.push({ id: 'project:tent-size', severity: 'error', code: 'tent-size', message: `Each tent face is only ${face.toFixed(1)} mm high. Use a taller flat sheet or a smaller base / glue tab.` });
  }
  if (p.productType === 'monthly' && p.carrier.enabled) {
    const c = p.carrier;
    if (c.pad.x < 0 || c.pad.y < 0 || c.pad.x + c.pad.w > c.widthMm + 0.01 || c.pad.y + c.pad.h > c.heightMm + 0.01)
      issues.push({ id: 'project:pad-zone', severity: 'error', code: 'pad-zone', message: 'The tear-off pad zone lies outside the carrier card.' });
    if (Math.abs(c.pad.w - p.widthMm) > 1 || Math.abs(c.pad.h - p.heightMm) > 1)
      issues.push({ id: 'project:pad-size', severity: 'warning', code: 'pad-size', message: `The pad leaves are ${p.widthMm} × ${p.heightMm} mm but the pad zone on the carrier is ${c.pad.w} × ${c.pad.h} mm.` });
  }
  return issues;
}

export function collectIssues(doc: ProjectDoc, layouts: PageLayout[], fonts: FontProvider): Issue[] {
  return [...projectChecks(doc, fonts), ...layouts.flatMap((l) => l.issues)];
}

export function blockingIssues(issues: Issue[]): Issue[] {
  return issues.filter((i) => i.severity === 'error');
}
export function unacknowledgedWarnings(issues: Issue[], acknowledged: string[]): Issue[] {
  const a = new Set(acknowledged);
  return issues.filter((i) => i.severity === 'warning' && !a.has(i.id));
}
